import { ObjectSchema } from '@framework/validation';
import * as React from 'react';
import { useMemo, useRef } from 'react';
import { IFormTransformOnSubmitFn } from '../IFormTransformOnSubmitFn';
import {
  InvalidFormSubject,
  createInvalidFormSubject,
} from './InvalidFormSubject';

export interface IFormAugmentedContext {
  schema: ObjectSchema<any>;
  validationContext?: any;
  /**
   * Subject per comunicare l'evento di Submit con i risultati di validazione
   */
  invalidSubject: InvalidFormSubject;

  /**
   * Funzione che permette di inviare il form.
   * Permette di chiamare programmaticamente la onSubmit.
   */
  submitForm: React.MutableRefObject<
    | null
    | ((
        event?: React.BaseSyntheticEvent,
        transformOnSubmit?: IFormTransformOnSubmitFn,
      ) => Promise<void>)
  >;

  /**
   * Funzione di onSubmit del form (a differenza di submitForm non attiva la validazione dei campi)
   */
  onSubmit: React.MutableRefObject<
    null | ((data: any, e: any) => void | Promise<void>)
  >;
}

export const FormAugmentedContext = React.createContext<IFormAugmentedContext>(
  null as any,
);

/**
 * Crea il context aumentato del Form corrente.
 * Utilizziamo la memo per evitare che il context aumentato venga ricreato
 * ad ogni render.
 */
export function FormAugmentedContextProvider(props: {
  schema: ObjectSchema<any>;
  validationContext?: any;
  children: React.ReactNode;
}) {
  const { schema, validationContext, children } = props;

  const submitForm = useRef(null as any);
  const onSubmit = useRef(null as any);

  // Subject per comunicare l'evento di Submit con i risultati di validazione
  const invalidSubject = useRef(createInvalidFormSubject());

  const value = useMemo(
    () => ({
      schema,
      validationContext,
      invalidSubject: invalidSubject.current,
      submitForm,
      onSubmit,
    }),
    [schema, validationContext],
  );

  return (
    <FormAugmentedContext.Provider value={value}>
      {children}
    </FormAugmentedContext.Provider>
  );
}

/**
 * Ottiene il context aumentato del Form in cui ci troviamo
 */
export function useFormAugmentedContext() {
  return React.useContext(FormAugmentedContext);
}
